import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { toast } from 'react-toastify';
import { sendHRReportEmailApi } from '../../redux/apis/hr';

const SendReportDialog = ({
  show,
  onHide,
  periodLabel,
  selectedTechNames = [],
  filterParams = {}
}) => {
  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [toInput, setToInput] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [otherTask, setOtherTask] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [recentEmails, setRecentEmails] = useState([]);

  // Load recent emails on show
  useEffect(() => {
    if (show) {
      const stored = JSON.parse(localStorage.getItem('ims_recent_emails') || '[]');
      setRecentEmails(stored);
      // Reset inputs on open
      setToEmails([]);
      setCcEmails([]);
      setToInput('');
      setCcInput('');
      setOtherTask('');
    }
  }, [show]);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const commitEmail = (rawValue, isCc = false) => {
    const trimmed = rawValue ? rawValue.trim() : '';
    if (!trimmed) return;

    // Split by common delimiters (comma, semicolon, space) in case of paste
    const parts = trimmed
      .split(/[,\s;]+/)
      .map((item) => item.trim())
      .filter(Boolean);

    const validEmails = [];
    for (const email of parts) {
      if (!emailRegex.test(email)) {
        toast.error(`"${email}" is not a valid email address`, {
          position: toast.POSITION.BOTTOM_RIGHT
        });
        return; // Halt and let the user correct
      }
      validEmails.push(email);
    }

    if (isCc) {
      const updated = Array.from(new Set([...ccEmails, ...validEmails]));
      setCcEmails(updated);
      setCcInput(''); // Clear the input field
    } else {
      const updated = Array.from(new Set([...toEmails, ...validEmails]));
      setToEmails(updated);
      setToInput(''); // Clear the input field
    }
  };

  const handleKeyDown = (e, isCc = false) => {
    // If they press Enter, Tab, Space, Semicolon or Comma
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ' ' || e.key === ';' || e.key === ',') {
      e.preventDefault();
      const rawValue = isCc ? ccInput : toInput;
      commitEmail(rawValue, isCc);
    }
  };

  const handleBlur = (isCc = false) => {
    const rawValue = isCc ? ccInput : toInput;
    commitEmail(rawValue, isCc);
  };

  const handleAddRecent = (email, isCc = false) => {
    if (isCc) {
      if (!ccEmails.includes(email)) {
        setCcEmails([...ccEmails, email]);
      }
    } else {
      if (!toEmails.includes(email)) {
        setToEmails([...toEmails, email]);
      }
    }
  };

  const handleRemoveEmail = (idx, isCc = false) => {
    if (isCc) {
      setCcEmails(ccEmails.filter((_, i) => i !== idx));
    } else {
      setToEmails(toEmails.filter((_, i) => i !== idx));
    }
  };

  const handleRemoveRecentEmail = (emailToRemove) => {
    const updated = recentEmails.filter((email) => email !== emailToRemove);
    setRecentEmails(updated);
    localStorage.setItem('ims_recent_emails', JSON.stringify(updated));
  };

  const handleSend = async () => {
    // Commit any remaining text in the inputs first
    let finalToEmails = [...toEmails];
    let finalCcEmails = [...ccEmails];

    // Validate and commit To input if exists
    if (toInput.trim()) {
      const parts = toInput
        .trim()
        .split(/[,\s;]+/)
        .map((e) => e.trim())
        .filter(Boolean);
      for (const email of parts) {
        if (!emailRegex.test(email)) {
          toast.error(`"${email}" is not a valid email address`, {
            position: toast.POSITION.BOTTOM_RIGHT
          });
          return;
        }
        if (!finalToEmails.includes(email)) {
          finalToEmails.push(email);
        }
      }
    }

    // Validate and commit CC input if exists
    if (ccInput.trim()) {
      const parts = ccInput
        .trim()
        .split(/[,\s;]+/)
        .map((e) => e.trim())
        .filter(Boolean);
      for (const email of parts) {
        if (!emailRegex.test(email)) {
          toast.error(`"${email}" is not a valid email address`, {
            position: toast.POSITION.BOTTOM_RIGHT
          });
          return;
        }
        if (!finalCcEmails.includes(email)) {
          finalCcEmails.push(email);
        }
      }
    }

    if (finalToEmails.length === 0) {
      toast.error('At least one recipient email (To) is required', {
        position: toast.POSITION.BOTTOM_RIGHT
      });
      return;
    }

    const { duration, startDate, endDate, technology, hrId } = filterParams;

    const requestData = {
      email: finalToEmails.join(', '),
      cc: finalCcEmails.join(', '),
      duration,
      hrId
    };

    if (technology) {
      requestData.technology = technology;
    }

    if (duration === 'custom') {
      requestData.startDate = startDate;
      requestData.endDate = endDate;
    }

    const trimmedOtherTask = otherTask ? otherTask.trim() : '';
    if (trimmedOtherTask) {
      requestData.otherTask = trimmedOtherTask;
    }

    try {
      setSendingEmail(true);
      const response = await sendHRReportEmailApi(requestData);
      if (response?.status === 200) {
        toast.success(response.data?.message || 'Report email sent successfully');

        // Save successfully sent emails to local storage
        const uniqueSent = Array.from(new Set([...finalToEmails, ...finalCcEmails]));
        const updatedRecent = Array.from(new Set([...uniqueSent, ...recentEmails])).slice(0, 20);
        localStorage.setItem('ims_recent_emails', JSON.stringify(updatedRecent));

        onHide();
      }
    } catch (err) {
      console.error('Failed to send report email:', err);
      toast.error('Failed to send report email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Filter out recent emails that are already added
  const availableRecentTo = useMemo(() => {
    return recentEmails.filter((email) => !toEmails.includes(email));
  }, [recentEmails, toEmails]);

  const availableRecentCc = useMemo(() => {
    return recentEmails.filter((email) => !ccEmails.includes(email));
  }, [recentEmails, ccEmails]);

  return (
    <Dialog
      header="Send Report via Email"
      visible={show}
      onHide={() => {
        if (!sendingEmail) {
          onHide();
        }
      }}
      className="rpt-mail-dialog"
      closable={!sendingEmail}>
      <div className="rpt-mail-dialog__content">
        {/* Selected Duration info */}
        <div>
          <label className="rpt-mail-dialog__label">Selected Duration</label>
          <div className="rpt-mail-dialog__info-box">{periodLabel}</div>
        </div>

        {/* Selected Technologies info */}
        <div>
          <label className="rpt-mail-dialog__label">Selected Technologies</label>
          <div className="rpt-mail-dialog__info-box rpt-mail-dialog__tech-box">
            {selectedTechNames.length > 0 ? (
              selectedTechNames.map((tech, idx) => (
                <span key={idx} className="rpt-mail-dialog__tech-chip">
                  {tech}
                </span>
              ))
            ) : (
              <span className="rpt-mail-dialog__tech-empty">All Technologies</span>
            )}
          </div>
        </div>

        {/* Recipient Email (To) */}
        <div>
          <label className="rpt-mail-dialog__label">
            Recipient Email (To) <span className="rpt-mail-dialog__req">*</span>
          </label>
          <span className="p-input-icon-left w-full">
            <i className="pi pi-envelope rpt-mail-dialog__input-icon" />
            <InputText
              id="toEmailsInput"
              value={toInput}
              onChange={(e) => setToInput(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, false)}
              onBlur={() => handleBlur(false)}
              placeholder="Enter emails (press Enter or space to add)"
              className="w-full rpt-mail-dialog__input"
              disabled={sendingEmail}
            />
          </span>

          {/* List of added To chips */}
          {toEmails.length > 0 && (
            <div className="rpt-mail-dialog__chips">
              {toEmails.map((email, idx) => (
                <span key={idx} className="email-chip">
                  {email}
                  <i
                    className="pi pi-times email-chip-close"
                    onClick={() => handleRemoveEmail(idx, false)}
                  />
                </span>
              ))}
            </div>
          )}

          {availableRecentTo.length > 0 && (
            <div className="rpt-mail-dialog__recent">
              <span className="rpt-mail-dialog__recent-label">Recent To:</span>
              {availableRecentTo.map((email) => (
                <div key={email} className="rpt-mail-dialog__recent-item">
                  <span
                    className="rpt-mail-dialog__recent-email"
                    onClick={() => handleAddRecent(email, false)}>
                    {email}
                  </span>
                  <i
                    className="pi pi-times rpt-mail-dialog__recent-remove"
                    onClick={() => handleRemoveRecentEmail(email)}
                    title="Remove"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CC Email */}
        <div>
          <label className="rpt-mail-dialog__label">CC Email</label>
          <span className="p-input-icon-left w-full">
            <i className="pi pi-envelope rpt-mail-dialog__input-icon" />
            <InputText
              id="ccEmailsInput"
              value={ccInput}
              onChange={(e) => setCcInput(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, true)}
              onBlur={() => handleBlur(true)}
              placeholder="Enter emails (press Enter or space to add)"
              className="w-full rpt-mail-dialog__input"
              disabled={sendingEmail}
            />
          </span>

          {/* List of added CC chips */}
          {ccEmails.length > 0 && (
            <div className="rpt-mail-dialog__chips">
              {ccEmails.map((email, idx) => (
                <span key={idx} className="email-chip">
                  {email}
                  <i
                    className="pi pi-times email-chip-close"
                    onClick={() => handleRemoveEmail(idx, true)}
                  />
                </span>
              ))}
            </div>
          )}

          {availableRecentCc.length > 0 && (
            <div className="rpt-mail-dialog__recent">
              <span className="rpt-mail-dialog__recent-label">Recent CC:</span>
              {availableRecentCc.map((email) => (
                <div key={email} className="rpt-mail-dialog__recent-item">
                  <span
                    className="rpt-mail-dialog__recent-email"
                    onClick={() => handleAddRecent(email, true)}>
                    {email}
                  </span>
                  <i
                    className="pi pi-times rpt-mail-dialog__recent-remove"
                    onClick={() => handleRemoveRecentEmail(email)}
                    title="Remove"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Other Task (Optional) */}
        <div>
          <label className="rpt-mail-dialog__label">Other Task (Optional)</label>
          <InputTextarea
            id="otherTaskInput"
            value={otherTask}
            onChange={(e) => setOtherTask(e.target.value)}
            placeholder="Enter any other tasks or notes..."
            rows={3}
            className="w-full rpt-mail-dialog__textarea"
            disabled={sendingEmail}
          />
        </div>

        {/* Action Buttons */}
        <div className="rpt-mail-dialog__actions">
          <Button
            label="Cancel"
            className="p-button-text p-button-sm rpt-mail-dialog__btn-cancel"
            onClick={onHide}
            disabled={sendingEmail}
          />
          <Button
            label="Send"
            icon="pi pi-send"
            className="p-button-sm rpt-mail-dialog__btn-send"
            onClick={handleSend}
            disabled={sendingEmail}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default SendReportDialog;
